// Fechamento de período (regra 9): prévia dos saldos do mês, bloqueios (impedem) e avisos
// (exigem confirmação), e o botão de fechar — que grava o snapshot dos saldos. Período fechado
// é imutável; reabrir é só admin, só o último mês fechado, com motivo.
import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { AlertTriangle, CheckCircle2, Lock, LockOpen, XOctagon } from "lucide-react";
import { getEstoquePeriodos, getEstoquePreviaFechamento, getRoleFromToken, postEstoqueFecharPeriodo, postEstoqueReabrirPeriodo } from "../../api";
import { permissoesEstoque } from "../../constants/estoque";
import EstoqueLayout from "./EstoqueLayout";
import { AvisoSemAbertura, MESES, formatData, mensagemErro, useContextoEstoque } from "./comum";
import { AvisoErro, Campo, Modal, btnStyle, formatBRL, formatQtd, inputStyle, palette, useIsMobile } from "./ui";

const TOTAIS = [
  ["valor_inicial", "Saldo inicial"],
  ["valor_entradas", "Entradas"],
  ["valor_saidas", "Saídas"],
  ["valor_ajustes", "Ajustes"],
  ["valor_final", "Saldo final"],
];

function Pendencia({ C, tipo, p }) {
  const [aberta, setAberta] = useState(false);
  const cor = tipo === "bloqueio" ? C.danger : C.warning;
  const Icone = tipo === "bloqueio" ? XOctagon : AlertTriangle;
  return (
    <div style={{ border: `1px solid ${cor}`, background: `${cor}10`, borderRadius: 8, padding: "8px 12px" }}>
      <div style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 13, color: cor, fontWeight: 700 }}>
        <Icone size={16} /> <span style={{ flex: 1 }}>{p.mensagem}</span>
        {p.itens?.length > 0 && (
          <button onClick={() => setAberta((v) => !v)} style={{ background: "none", border: "none", color: cor, cursor: "pointer", fontSize: 12, fontWeight: 700, fontFamily: "inherit" }}>{aberta ? "ocultar" : "ver"}</button>
        )}
      </div>
      {aberta && <ul style={{ margin: "6px 0 0", paddingLeft: 26, fontSize: 12, color: C.text }}>{p.itens.map((i) => <li key={i}>{i}</li>)}</ul>}
    </div>
  );
}

export default function FechamentoView() {
  const [isLight, setIsLight] = useState(true);
  const C = palette(isLight);
  const isMobile = useIsMobile();
  const navigate = useNavigate();
  const perm = permissoesEstoque(getRoleFromToken());
  const { contexto, erro: erroContexto } = useContextoEstoque();
  const [periodos, setPeriodos] = useState(null);
  const [sel, setSel] = useState(null);
  const [previa, setPrevia] = useState(null);
  const [erro, setErro] = useState(null);
  const [confirmarAvisos, setConfirmarAvisos] = useState(false);
  const [modal, setModal] = useState(null); // "fechar" | "reabrir"
  const [motivo, setMotivo] = useState("");
  const [salvando, setSalvando] = useState(false);

  const carregarPeriodos = useCallback(() => getEstoquePeriodos().then((ps) => {
    setPeriodos(ps);
    setSel((atual) => atual || (() => {
      const abertos = ps.filter((p) => p.status === "ABERTO");
      const alvo = abertos[abertos.length - 1] || ps[0]; // o mais antigo ainda aberto
      return alvo ? { ano: alvo.ano, mes: alvo.mes } : null;
    })());
  }).catch((e) => setErro(e)), []);
  useEffect(() => { carregarPeriodos(); }, [carregarPeriodos]);
  useEffect(() => {
    if (!sel) return;
    setPrevia(null);
    setConfirmarAvisos(false);
    getEstoquePreviaFechamento(sel.ano, sel.mes).then(setPrevia).catch((e) => setErro(e));
  }, [sel]);

  async function executar(fn) {
    setSalvando(true);
    setErro(null);
    try {
      setPrevia(await fn());
      setModal(null);
      setMotivo("");
      carregarPeriodos();
    } catch (e) {
      setErro(e);
    } finally {
      setSalvando(false);
    }
  }

  const fechado = previa?.status === "FECHADO";
  const itens = (previa?.relatorio.itens || []).filter((i) => [i.qtd_inicial, i.qtd_entradas, i.qtd_saidas, i.qtd_ajustes, i.qtd_final].some((v) => Number(v) !== 0));
  const podeClicarFechar = previa && previa.pode_fechar && (!previa.avisos.length || confirmarAvisos);

  return (
    <EstoqueLayout titulo="Fechamento de Período" isLight={isLight} setIsLight={setIsLight}>
      <AvisoErro C={C} onClose={() => setErro(null)}>{(erro && mensagemErro(erro)) || erroContexto}</AvisoErro>
      {!contexto || !periodos ? null : !contexto.abertura_concluida ? <AvisoSemAbertura C={C} /> : (
        <div style={{ display: "flex", flexDirection: isMobile ? "column" : "row", gap: 16, alignItems: "flex-start" }}>
          <div style={{ display: "flex", flexDirection: isMobile ? "row" : "column", gap: 6, width: isMobile ? "100%" : 220, flexShrink: 0, overflowX: isMobile ? "auto" : "visible" }}>
            {periodos.map((p) => {
              const ativo = sel && sel.ano === p.ano && sel.mes === p.mes;
              return (
                <button key={`${p.ano}-${p.mes}`} onClick={() => setSel({ ano: p.ano, mes: p.mes })}
                  style={{ display: "flex", alignItems: "center", gap: 8, padding: "10px 12px", minHeight: 44, borderRadius: 10, border: `1px solid ${ativo ? C.brand : C.border}`, background: ativo ? C.bgActive : C.card, cursor: "pointer", fontFamily: "inherit", color: C.text, textAlign: "left", flexShrink: 0 }}>
                  {p.status === "FECHADO" ? <Lock size={15} color={C.success} /> : <LockOpen size={15} color={C.muted} />}
                  <div>
                    <div style={{ fontSize: 13, fontWeight: 700, whiteSpace: "nowrap" }}>{MESES[p.mes - 1]} {p.ano}</div>
                    <div style={{ fontSize: 11, color: C.muted, whiteSpace: "nowrap" }}>{p.status === "FECHADO" ? `Fechado · ${formatBRL(p.valor_final_snapshot)}` : "Aberto"}</div>
                  </div>
                </button>
              );
            })}
          </div>

          <div style={{ flex: 1, minWidth: 0, width: "100%" }}>
            {!previa ? <div style={{ color: C.muted, fontSize: 13 }}>Carregando...</div> : (
              <>
                <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap", marginBottom: 12 }}>
                  <h2 style={{ margin: 0, fontSize: 18 }}>{MESES[previa.mes - 1]} de {previa.ano}</h2>
                  <span style={{ fontSize: 12, fontWeight: 700, padding: "3px 10px", borderRadius: 999, background: fechado ? `${C.success}1F` : `${C.muted}1F`, color: fechado ? C.success : C.muted }}>{fechado ? "Fechado" : "Aberto"}</span>
                  <button onClick={() => navigate("/estoque/relatorio")} style={{ ...btnStyle(C.brand, true), padding: "6px 10px", minHeight: 34, fontSize: 12, marginLeft: "auto" }}>Abrir relatório do período</button>
                </div>

                {fechado && (
                  <div style={{ fontSize: 13, background: C.card, border: `1px solid ${C.border}`, borderRadius: 10, padding: 12, marginBottom: 12, display: "flex", flexDirection: "column", gap: 4 }}>
                    <span>Fechado {previa.fechado_por_nome ? `por ${previa.fechado_por_nome} ` : ""}em {formatData(previa.fechado_em)}.</span>
                    {previa.reaberto_em && <span style={{ color: C.muted }}>Já foi reaberto em {formatData(previa.reaberto_em)}{previa.reaberto_por_nome ? ` por ${previa.reaberto_por_nome}` : ""}: {previa.motivo_reabertura}</span>}
                    <span style={{ display: "flex", alignItems: "center", gap: 6, color: previa.snapshot_confere ? C.success : C.danger, fontWeight: 700 }}>
                      {previa.snapshot_confere ? <CheckCircle2 size={15} /> : <XOctagon size={15} />}
                      {previa.snapshot_confere ? "Snapshot confere com as movimentações" : `Snapshot diverge das movimentações: ${previa.snapshot_divergentes.join(", ")}`}
                    </span>
                  </div>
                )}

                {!fechado && (previa.bloqueios.length > 0 || previa.avisos.length > 0) && (
                  <div style={{ display: "flex", flexDirection: "column", gap: 6, marginBottom: 12 }}>
                    {previa.bloqueios.map((b) => <Pendencia key={b.codigo} C={C} tipo="bloqueio" p={b} />)}
                    {previa.avisos.map((a) => <Pendencia key={a.codigo} C={C} tipo="aviso" p={a} />)}
                  </div>
                )}
                {!fechado && !previa.bloqueios.length && !previa.avisos.length && (
                  <div style={{ display: "flex", gap: 6, alignItems: "center", color: C.success, fontSize: 13, fontWeight: 700, marginBottom: 12 }}><CheckCircle2 size={16} /> Sem pendências — pronto para fechar.</div>
                )}

                <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(140px, 1fr))", gap: 10, marginBottom: 12 }}>
                  {TOTAIS.map(([campo, rotulo]) => (
                    <div key={campo} style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 12, padding: 12 }}>
                      <div style={{ fontSize: 10, fontWeight: 700, color: C.muted, textTransform: "uppercase" }}>{rotulo}</div>
                      <div style={{ fontSize: 18, fontWeight: 800 }}>{formatBRL(previa.relatorio.totais[campo])}</div>
                    </div>
                  ))}
                </div>

                <div style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 12, overflow: "auto", maxHeight: "42vh", marginBottom: 12 }}>
                  <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
                    <thead>
                      <tr>
                        {["Produto", ...(isMobile ? [] : ["Inicial", "Entradas", "Saídas", "Ajustes"]), "Final", "Valor final"].map((h, i) => (
                          <th key={h} style={{ padding: "8px 10px", fontSize: 11, fontWeight: 700, color: C.muted, textTransform: "uppercase", textAlign: i === 0 ? "left" : "right", borderBottom: `1px solid ${C.border}`, position: "sticky", top: 0, background: C.card, whiteSpace: "nowrap" }}>{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {itens.map((i) => (
                        <tr key={i.produto_id}>
                          <td style={{ padding: "7px 10px", borderBottom: `1px solid ${C.border}` }}>{i.produto_nome} <span style={{ fontSize: 11, color: C.muted }}>({i.unidade_sigla})</span></td>
                          {!isMobile && ["qtd_inicial", "qtd_entradas", "qtd_saidas", "qtd_ajustes"].map((c) => (
                            <td key={c} style={{ padding: "7px 10px", borderBottom: `1px solid ${C.border}`, textAlign: "right" }}>{formatQtd(i[c])}</td>
                          ))}
                          <td style={{ padding: "7px 10px", borderBottom: `1px solid ${C.border}`, textAlign: "right", fontWeight: 700, color: Number(i.qtd_final) < 0 ? C.danger : C.text }}>{formatQtd(i.qtd_final)}</td>
                          <td style={{ padding: "7px 10px", borderBottom: `1px solid ${C.border}`, textAlign: "right", fontWeight: 700, whiteSpace: "nowrap" }}>{formatBRL(i.valor_final)}</td>
                        </tr>
                      ))}
                      {itens.length === 0 && <tr><td colSpan={7} style={{ padding: 20, textAlign: "center", color: C.muted }}>Sem saldo nem movimento no período.</td></tr>}
                    </tbody>
                  </table>
                </div>

                {!fechado && perm.financeiro && (
                  <div style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 12, padding: 14, display: "flex", flexDirection: "column", gap: 10 }}>
                    {previa.avisos.length > 0 && previa.pode_fechar && (
                      <label style={{ display: "flex", alignItems: "flex-start", gap: 8, fontSize: 13, cursor: "pointer" }}>
                        <input type="checkbox" checked={confirmarAvisos} onChange={(e) => setConfirmarAvisos(e.target.checked)} style={{ width: 18, height: 18, marginTop: 1 }} />
                        Li os {previa.avisos.length} aviso(s) acima e quero fechar o período mesmo assim.
                      </label>
                    )}
                    <button onClick={() => setModal("fechar")} disabled={!podeClicarFechar} style={{ ...btnStyle(C.success), opacity: podeClicarFechar ? 1 : 0.5, cursor: podeClicarFechar ? "pointer" : "not-allowed" }}>
                      <Lock size={15} /> Fechar {MESES[previa.mes - 1].toLowerCase()}
                    </button>
                  </div>
                )}
                {fechado && perm.admin && previa.pode_reabrir && (
                  <button onClick={() => setModal("reabrir")} style={btnStyle(C.danger, true)}><LockOpen size={15} /> Reabrir período</button>
                )}
                {fechado && perm.admin && !previa.pode_reabrir && <div style={{ fontSize: 12, color: C.muted }}>Só o último mês fechado pode ser reaberto.</div>}
              </>
            )}
          </div>
        </div>
      )}

      {modal === "fechar" && previa && (
        <Modal C={C} titulo={`Fechar ${MESES[previa.mes - 1].toLowerCase()} de ${previa.ano}?`} onClose={() => setModal(null)} largura={460}
          rodape={<>
            <button onClick={() => setModal(null)} style={btnStyle(C.muted, true)}>Voltar</button>
            <button disabled={salvando} onClick={() => executar(() => postEstoqueFecharPeriodo(previa.ano, previa.mes, { confirmar_avisos: confirmarAvisos }))} style={btnStyle(C.success)}>{salvando ? "Fechando..." : "Fechar período"}</button>
          </>}>
          <p style={{ margin: 0, fontSize: 14, lineHeight: 1.5 }}>
            O saldo final de <strong>{formatBRL(previa.relatorio.totais.valor_final)}</strong> será gravado como snapshot. Depois de fechado, nenhum lançamento com data neste mês
            pode ser criado ou cancelado — correções entram como ajuste no período aberto.
          </p>
        </Modal>
      )}
      {modal === "reabrir" && previa && (
        <Modal C={C} titulo="Reabrir período?" onClose={() => setModal(null)} largura={460}
          rodape={<>
            <button onClick={() => setModal(null)} style={btnStyle(C.muted, true)}>Voltar</button>
            <button disabled={salvando || !motivo.trim()} onClick={() => executar(() => postEstoqueReabrirPeriodo(previa.ano, previa.mes, motivo.trim()))} style={{ ...btnStyle(C.danger), opacity: motivo.trim() ? 1 : 0.5 }}>Reabrir</button>
          </>}>
          <p style={{ marginTop: 0, fontSize: 13, color: C.muted, lineHeight: 1.5 }}>O snapshot deste mês é apagado e volta a ser gravado no próximo fechamento. A reabertura fica registrada no log.</p>
          <Campo label="Motivo da reabertura" C={C} obrigatorio><input value={motivo} onChange={(e) => setMotivo(e.target.value)} style={inputStyle(C)} autoFocus /></Campo>
        </Modal>
      )}
    </EstoqueLayout>
  );
}
